import React from "react";

/* ═══════════════════════════════════════════════════════════════
   Plant Biology — 8 ANIMATED templates
   flower_bloom, tree_growth_rings, root_network,
   seed_sprout, leaf_photosynthesis, pollen_drift,
   fruit_stages, moss_spores
   ═══════════════════════════════════════════════════════════════ */

/* ─── 1. Flower Bloom ─────────────────────────────────────── */
export const FlowerBloom: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Flower Anatomy";
  const animationProgress = (props.animationProgress as number) || 0;
  const petalOpen = animationProgress * 45;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Stem */}
      <line x1="400" y1="300" x2="400" y2="520" stroke="#2e7d32" strokeWidth="6" />
      {/* Leaves */}
      <ellipse cx="370" cy="420" rx="40" ry="15" fill="#4caf50" opacity="0.7" transform="rotate(-30,370,420)">
        <animateTransform attributeName="transform" type="rotate" values="-30,370,420;-25,370,420;-30,370,420" dur="3s" repeatCount="indefinite" />
      </ellipse>
      <ellipse cx="430" cy="380" rx="35" ry="13" fill="#4caf50" opacity="0.7" transform="rotate(25,430,380)">
        <animateTransform attributeName="transform" type="rotate" values="25,430,380;30,430,380;25,430,380" dur="3.5s" repeatCount="indefinite" />
      </ellipse>
      {/* Petals opening */}
      {[0, 60, 120, 180, 240, 300].map((angle) => (
        <g key={angle} transform={`rotate(${angle}, 400, 260)`}>
          <ellipse cx="400" cy={260 - 30 - petalOpen * 0.6} rx="25" ry="45"
            fill="#e91e63" opacity={0.6 + animationProgress * 0.3}
            transform={`rotate(${petalOpen * 0.3}, 400, 260)`}>
            <animate attributeName="opacity" values={`${0.6 + animationProgress * 0.3};${0.7 + animationProgress * 0.3};${0.6 + animationProgress * 0.3}`}
              dur="2s" repeatCount="indefinite" />
          </ellipse>
        </g>
      ))}
      {/* Center (pistil/stamen) */}
      <circle cx="400" cy="260" r={15 + animationProgress * 5} fill="#f9a825" opacity="0.9">
        <animate attributeName="r" values={`${15 + animationProgress * 5};${18 + animationProgress * 5};${15 + animationProgress * 5}`} dur="1.5s" repeatCount="indefinite" />
      </circle>
      {/* Pollen particles */}
      {animationProgress > 0.5 && [0, 1, 2, 3, 4].map((i) => {
        const a = (i * 72) * Math.PI / 180;
        return (
          <circle key={i} cx={400 + Math.cos(a) * 30} cy={260 + Math.sin(a) * 30} r="3" fill="#fff9c4" opacity="0.5">
            <animate attributeName="cx" from={400 + Math.cos(a) * 30} to={400 + Math.cos(a) * 80}
              dur={`${2 + i * 0.3}s`} repeatCount="indefinite" />
            <animate attributeName="cy" from={260 + Math.sin(a) * 30} to={260 + Math.sin(a) * 80 - 30}
              dur={`${2 + i * 0.3}s`} repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.5;0" dur={`${2 + i * 0.3}s`} repeatCount="indefinite" />
          </circle>
        );
      })}
      <text x="400" y="560" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 2. Tree Growth Rings ────────────────────────────────── */
export const TreeGrowthRings: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Tree Growth Rings";
  const activeIndex = (props.activeIndex as number) ?? -1;
  const years = (props.years as string[]) || ["Year 1", "Year 5", "Year 10", "Year 20", "Year 50"];
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Cross section */}
      {[200, 160, 120, 80, 40].map((r, i) => (
        <circle key={i} cx="400" cy="280" r={r}
          fill="none" stroke={i === activeIndex ? "#f9a825" : `hsl(30, ${40 + i * 10}%, ${20 + i * 5}%)`}
          strokeWidth={i === activeIndex ? 4 : 2}>
          {i === activeIndex && (
            <animate attributeName="stroke-width" values="4;6;4" dur="1s" repeatCount="indefinite" />
          )}
        </circle>
      ))}
      {/* Center (pith) */}
      <circle cx="400" cy="280" r="15" fill="#5d4037" />
      {/* Bark */}
      <circle cx="400" cy="280" r="210" fill="none" stroke="#3e2723" strokeWidth="8" />
      {/* Year labels */}
      {years.map((yr, i) => {
        const r = 200 - i * 40;
        return (
          <text key={i} x={400 + r + 15} y="280"
            fill={i === activeIndex ? "#f9a825" : "#7f8c8d"} fontSize="10">{yr}</text>
        );
      })}
      {/* Growth indicator */}
      <path d="M400,70 L400,60" stroke="#4caf50" strokeWidth="2" markerEnd="url(#tgr-arr)">
        <animate attributeName="d" values="M400,70 L400,60;M400,70 L400,50;M400,70 L400,60" dur="2s" repeatCount="indefinite" />
      </path>
      <defs>
        <marker id="tgr-arr" viewBox="0 0 10 10" refX="5" refY="10" markerWidth="5" markerHeight="5" orient="auto">
          <path d="M0,10 L5,0 L10,10Z" fill="#4caf50" />
        </marker>
      </defs>
      <text x="400" y="30" textAnchor="middle" fill="#4caf50" fontSize="12">Growth</text>
      <text x="400" y="540" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 3. Root Network ─────────────────────────────────────── */
export const RootNetwork: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Root System";
  const animationProgress = (props.animationProgress as number) || 0;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Sky / above ground */}
      <rect x="0" y="0" width="800" height="200" fill="#1b5e20" opacity="0.15" rx="16" />
      {/* Ground line */}
      <line x1="0" y1="200" x2="800" y2="200" stroke="#5d4037" strokeWidth="3" />
      {/* Tree trunk above */}
      <rect x="380" y="100" width="40" height="100" fill="#5d4037" />
      {/* Canopy */}
      <ellipse cx="400" cy="80" rx="80" ry="60" fill="#2e7d32" opacity="0.6">
        <animate attributeName="rx" values="80;85;80" dur="3s" repeatCount="indefinite" />
      </ellipse>
      {/* Root system below ground */}
      {/* Tap root */}
      <path d={`M400,200 L400,${200 + animationProgress * 300}`}
        fill="none" stroke="#8d6e63" strokeWidth="6" strokeLinecap="round" />
      {/* Lateral roots */}
      {[
        { path: "M400,250 Q350,260 280,280 Q200,300 150,340", delay: 0.2 },
        { path: "M400,250 Q450,260 520,280 Q600,300 650,340", delay: 0.2 },
        { path: "M400,320 Q340,340 260,380 Q200,410 160,440", delay: 0.4 },
        { path: "M400,320 Q460,340 540,380 Q600,410 640,440", delay: 0.4 },
        { path: "M400,400 Q350,420 300,460", delay: 0.6 },
        { path: "M400,400 Q450,420 500,460", delay: 0.6 },
      ].map((r, i) => (
        <path key={i} d={r.path} fill="none" stroke="#8d6e63" strokeWidth={4 - i * 0.4}
          strokeLinecap="round" opacity={animationProgress > r.delay ? 0.8 : 0.1}
          strokeDasharray={animationProgress > r.delay ? "none" : "200"}
          strokeDashoffset={animationProgress > r.delay ? 0 : 200}>
          <animate attributeName="stroke-dashoffset" from="200" to="0" dur="2s" fill="freeze" />
        </path>
      ))}
      {/* Root hairs */}
      {animationProgress > 0.5 && Array.from({ length: 12 }, (_, i) => {
        const x = 200 + i * 40;
        const y = 300 + (i % 4) * 40;
        return (
          <line key={i} x1={x} y1={y} x2={x + (i % 2 === 0 ? 15 : -15)} y2={y + 10}
            stroke="#a1887f" strokeWidth="1" opacity="0.4">
            <animate attributeName="opacity" values="0.2;0.5;0.2" dur={`${1.5 + i * 0.1}s`} repeatCount="indefinite" />
          </line>
        );
      })}
      {/* Water absorption arrows */}
      {animationProgress > 0.3 && [280, 520].map((x, i) => (
        <g key={i} opacity="0.3">
          <line x1={x} y1={350 + i * 20} x2={x + (i === 0 ? 20 : -20)} y2={330 + i * 20}
            stroke="#42a5f5" strokeWidth="2" markerEnd="url(#rn-arr)">
            <animate attributeName="opacity" values="0.1;0.4;0.1" dur="1.5s" repeatCount="indefinite" begin={`${i * 0.5}s`} />
          </line>
        </g>
      ))}
      <defs>
        <marker id="rn-arr" viewBox="0 0 10 10" refX="5" refY="10" markerWidth="5" markerHeight="5" orient="auto">
          <path d="M0,10 L5,0 L10,10Z" fill="#42a5f5" />
        </marker>
      </defs>
      <text x="400" y="560" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 4. Seed Sprout ──────────────────────────────────────── */
export const SeedSprout: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Seed Germination";
  const activeIndex = (props.activeIndex as number) ?? -1;
  const stages = (props.stages as string[]) || ["Seed", "Radicle", "Shoot", "Seedling", "Plant"];
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Soil line */}
      <rect x="0" y="360" width="800" height="200" fill="#3e2723" opacity="0.3" />
      <line x1="0" y1="360" x2="800" y2="360" stroke="#5d4037" strokeWidth="2" />
      {/* Stages */}
      {[
        { x: 100, seed: true, root: 0, shoot: 0 },
        { x: 240, seed: true, root: 40, shoot: 0 },
        { x: 380, seed: true, root: 60, shoot: 30 },
        { x: 520, seed: false, root: 80, shoot: 80 },
        { x: 660, seed: false, root: 100, shoot: 150 },
      ].map((s, i) => (
        <g key={i}>
          {/* Root */}
          {s.root > 0 && (
            <line x1={s.x} y1="360" x2={s.x} y2={360 + s.root}
              stroke="#8d6e63" strokeWidth="3" strokeLinecap="round">
              {i === activeIndex && <animate attributeName="stroke-width" values="3;5;3" dur="1s" repeatCount="indefinite" />}
            </line>
          )}
          {/* Seed */}
          {s.seed && (
            <ellipse cx={s.x} cy="360" rx="18" ry="12"
              fill={i === activeIndex ? "#f9a825" : "#8d6e63"}
              stroke={i === activeIndex ? "#fff" : "#6d4c41"} strokeWidth={i === activeIndex ? 2 : 1}>
              {i === activeIndex && <animate attributeName="rx" values="18;20;18" dur="1s" repeatCount="indefinite" />}
            </ellipse>
          )}
          {/* Shoot */}
          {s.shoot > 0 && (
            <g>
              <line x1={s.x} y1="360" x2={s.x} y2={360 - s.shoot}
                stroke="#4caf50" strokeWidth={i > 3 ? 4 : 2} strokeLinecap="round" />
              {i >= 3 && (
                <g>
                  <ellipse cx={s.x - 15} cy={360 - s.shoot + 20} rx="12" ry="6" fill="#4caf50" opacity="0.7" transform={`rotate(-30,${s.x - 15},${360 - s.shoot + 20})`}>
                    <animateTransform attributeName="transform" type="rotate" values={`-30,${s.x - 15},${360 - s.shoot + 20};-25,${s.x - 15},${360 - s.shoot + 20};-30,${s.x - 15},${360 - s.shoot + 20}`} dur="3s" repeatCount="indefinite" />
                  </ellipse>
                  <ellipse cx={s.x + 15} cy={360 - s.shoot + 35} rx="12" ry="6" fill="#4caf50" opacity="0.7" transform={`rotate(30,${s.x + 15},${360 - s.shoot + 35})`} />
                </g>
              )}
            </g>
          )}
          {/* Label */}
          <text x={s.x} y={490} textAnchor="middle"
            fill={i === activeIndex ? "#f9a825" : "#7f8c8d"} fontSize="11">{stages[i]}</text>
        </g>
      ))}
      {/* Water drops */}
      {[200, 450].map((x, i) => (
        <g key={i}>
          <circle cx={x} cy="300" r="4" fill="#42a5f5" opacity="0.3">
            <animate attributeName="cy" from="300" to="356" dur="1.5s" repeatCount="indefinite" begin={`${i * 0.5}s`} />
            <animate attributeName="opacity" values="0.3;0" dur="1.5s" repeatCount="indefinite" begin={`${i * 0.5}s`} />
          </circle>
        </g>
      ))}
      {/* Sun */}
      <circle cx="700" cy="80" r="30" fill="#fff9c4" opacity="0.4">
        <animate attributeName="r" values="30;33;30" dur="2s" repeatCount="indefinite" />
      </circle>
      <text x="400" y="550" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 5. Leaf Photosynthesis ──────────────────────────────── */
export const LeafPhotosynthesis: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Photosynthesis";
  const flowProgress = (props.flowProgress as number) || 0;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Large leaf shape */}
      <path d="M400,100 Q250,200 200,350 Q350,500 400,520 Q450,500 600,350 Q550,200 400,100"
        fill="#2e7d32" opacity="0.6" stroke="#1b5e20" strokeWidth="2" />
      {/* Leaf veins */}
      <line x1="400" y1="120" x2="400" y2="510" stroke="#1b5e20" strokeWidth="3" />
      {[200, 260, 320, 380, 440].map((y) => (
        <g key={y}>
          <line x1="400" y1={y} x2={320 - (y - 200) * 0.2} y2={y + 30} stroke="#1b5e20" strokeWidth="1.5" opacity="0.5" />
          <line x1="400" y1={y} x2={480 + (y - 200) * 0.2} y2={y + 30} stroke="#1b5e20" strokeWidth="1.5" opacity="0.5" />
        </g>
      ))}
      {/* Sun rays coming in */}
      {flowProgress > 0 && [0, 1, 2].map((i) => (
        <line key={i} x1={650 - i * 30} y1={80 + i * 30} x2={450 - i * 20} y2={200 + i * 40}
          stroke="#fff9c4" strokeWidth="2" opacity="0.2" strokeDasharray="8,6">
          <animate attributeName="stroke-dashoffset" from="14" to="0" dur="1s" repeatCount="indefinite" />
        </line>
      ))}
      <circle cx="680" cy="60" r="35" fill="#fff9c4" opacity="0.3">
        <animate attributeName="r" values="35;38;35" dur="2s" repeatCount="indefinite" />
      </circle>
      {/* CO₂ entering */}
      {flowProgress > 0.2 && (
        <g>
          <text x="120" y="250" fill="#42a5f5" fontSize="14" opacity="0.6">CO₂</text>
          <circle cx="160" cy="280" r="3" fill="#42a5f5" opacity="0.4">
            <animate attributeName="cx" from="160" to="300" dur="2s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.4;0" dur="2s" repeatCount="indefinite" />
          </circle>
        </g>
      )}
      {/* H₂O entering from below */}
      {flowProgress > 0.2 && (
        <g>
          <text x="380" y="560" fill="#1565c0" fontSize="14" opacity="0.6">H₂O</text>
          <circle cx="400" cy="530" r="3" fill="#1565c0" opacity="0.4">
            <animate attributeName="cy" from="530" to="400" dur="2s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.4;0" dur="2s" repeatCount="indefinite" />
          </circle>
        </g>
      )}
      {/* O₂ output */}
      {flowProgress > 0.5 && (
        <g>
          <text x="630" y="350" fill="#4caf50" fontSize="14" opacity="0.6">O₂</text>
          {[0, 1, 2].map((i) => (
            <circle key={i} cx="590" cy={350 + i * 20} r="4" fill="#4caf50" opacity="0.3">
              <animate attributeName="cx" from="590" to={650 + i * 20} dur={`${2 + i * 0.5}s`} repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.3;0" dur={`${2 + i * 0.5}s`} repeatCount="indefinite" />
            </circle>
          ))}
        </g>
      )}
      {/* Glucose output */}
      {flowProgress > 0.7 && (
        <text x="400" y="330" textAnchor="middle" fill="#f9a825" fontSize="13" opacity="0.7">
          C₆H₁₂O₆ (Glucose)
          <animate attributeName="opacity" values="0.5;0.9;0.5" dur="2s" repeatCount="indefinite" />
        </text>
      )}
      <text x="400" y="40" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 6. Pollen Drift ─────────────────────────────────────── */
export const PollenDrift: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Pollination";
  const phase = (props.phase as number) || 0;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Ground */}
      <rect x="0" y="460" width="800" height="140" fill="#2e7d32" opacity="0.2" />
      {/* Flower 1 (source) */}
      <g transform="translate(200, 380)">
        <line x1="0" y1="0" x2="0" y2="80" stroke="#2e7d32" strokeWidth="4" />
        {[0, 72, 144, 216, 288].map((a) => (
          <ellipse key={a} cx={Math.cos(a * Math.PI / 180) * 25} cy={Math.sin(a * Math.PI / 180) * 25 - 10}
            rx="15" ry="20" fill="#e91e63" opacity="0.7" transform={`rotate(${a}, ${Math.cos(a * Math.PI / 180) * 25}, ${Math.sin(a * Math.PI / 180) * 25 - 10})`} />
        ))}
        <circle cx="0" cy="-10" r="12" fill="#f9a825" />
      </g>
      {/* Flower 2 (receiver) */}
      <g transform="translate(600, 400)">
        <line x1="0" y1="0" x2="0" y2="60" stroke="#2e7d32" strokeWidth="4" />
        {[0, 72, 144, 216, 288].map((a) => (
          <ellipse key={a} cx={Math.cos(a * Math.PI / 180) * 22} cy={Math.sin(a * Math.PI / 180) * 22 - 10}
            rx="13" ry="18" fill="#9c27b0" opacity="0.6" transform={`rotate(${a}, ${Math.cos(a * Math.PI / 180) * 22}, ${Math.sin(a * Math.PI / 180) * 22 - 10})`} />
        ))}
        <circle cx="0" cy="-10" r="10" fill="#f9a825" opacity="0.6" />
      </g>
      {/* Bee */}
      <g>
        <ellipse cx="0" cy="0" rx="15" ry="10" fill="#f9a825" stroke="#f57f17" strokeWidth="1">
          <animateMotion dur="6s" repeatCount="indefinite"
            path="M200,350 Q300,250 400,280 Q500,310 600,370" />
        </ellipse>
        {/* Wings */}
        <ellipse cx="0" cy="-12" rx="10" ry="6" fill="#ecf0f1" opacity="0.4">
          <animateMotion dur="6s" repeatCount="indefinite"
            path="M200,350 Q300,250 400,280 Q500,310 600,370" />
          <animate attributeName="ry" values="6;3;6" dur="0.1s" repeatCount="indefinite" />
        </ellipse>
      </g>
      {/* Pollen particles */}
      {Array.from({ length: 15 }, (_, i) => {
        const startX = 200 + Math.random() * 30;
        const startY = 340 + Math.random() * 30;
        return (
          <circle key={i} cx={startX} cy={startY} r="2" fill="#fff9c4" opacity="0.4">
            <animate attributeName="cx" from={startX} to={startX + 100 + i * 20}
              dur={`${4 + (i % 4)}s`} begin={`${i * 0.4}s`} repeatCount="indefinite" />
            <animate attributeName="cy" from={startY} to={startY - 20 + Math.sin(phase + i) * 40}
              dur={`${4 + (i % 4)}s`} begin={`${i * 0.4}s`} repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.4;0" dur={`${4 + (i % 4)}s`} begin={`${i * 0.4}s`} repeatCount="indefinite" />
          </circle>
        );
      })}
      {/* Wind arrows */}
      {[250, 320, 390].map((y, i) => (
        <line key={i} x1={250 + i * 30} y1={y} x2={350 + i * 30} y2={y - 10}
          stroke="#ecf0f1" strokeWidth="1" opacity="0.15" markerEnd="url(#pd-wind)">
          <animate attributeName="x1" from={250 + i * 30} to={350 + i * 30} dur="3s" repeatCount="indefinite" />
          <animate attributeName="x2" from={350 + i * 30} to={450 + i * 30} dur="3s" repeatCount="indefinite" />
        </line>
      ))}
      <defs>
        <marker id="pd-wind" viewBox="0 0 10 10" refX="10" refY="5" markerWidth="4" markerHeight="4" orient="auto">
          <path d="M0,0 L10,5 L0,10Z" fill="#ecf0f1" />
        </marker>
      </defs>
      <text x="400" y="550" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 7. Fruit Stages ─────────────────────────────────────── */
export const FruitStages: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Fruit Development";
  const activeIndex = (props.activeIndex as number) ?? -1;
  const stages = (props.stages as string[]) || ["Flower", "Pollinated", "Young Fruit", "Mature Fruit", "Ripe"];
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Timeline arrow */}
      <line x1="80" y1="300" x2="720" y2="300" stroke="#2c3e50" strokeWidth="3" markerEnd="url(#fs-arr)" />
      <defs>
        <marker id="fs-arr" viewBox="0 0 10 10" refX="10" refY="5" markerWidth="6" markerHeight="6" orient="auto">
          <path d="M0,0 L10,5 L0,10Z" fill="#2c3e50" />
        </marker>
      </defs>
      {/* Stages */}
      {[
        { x: 120, emoji: "🌸", size: 20 },
        { x: 260, emoji: "🌼", size: 24 },
        { x: 400, emoji: "🫒", size: 28 },
        { x: 540, emoji: "🍏", size: 32 },
        { x: 680, emoji: "🍎", size: 36 },
      ].map((s, i) => (
        <g key={i}>
          <circle cx={s.x} cy="300" r={i === activeIndex ? 45 : 38}
            fill={i === activeIndex ? "#1c2833" : "#0d1117"}
            stroke={i === activeIndex ? "#4caf50" : "#2c3e50"} strokeWidth={i === activeIndex ? 3 : 1.5}>
            {i === activeIndex && <animate attributeName="r" values="45;50;45" dur="1.2s" repeatCount="indefinite" />}
          </circle>
          <text x={s.x} y="310" textAnchor="middle" fontSize={s.size}>{s.emoji}</text>
          <text x={s.x} y="365" textAnchor="middle" fill={i === activeIndex ? "#4caf50" : "#7f8c8d"} fontSize="11">{stages[i]}</text>
        </g>
      ))}
      <text x="400" y="180" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 8. Moss Spores ──────────────────────────────────────── */
export const MossSpores: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Moss Life Cycle";
  const phase = (props.phase as number) || 0;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Ground / rock surface */}
      <rect x="100" y="400" width="600" height="100" rx="12" fill="#37474f" />
      {/* Moss carpet */}
      {Array.from({ length: 30 }, (_, i) => (
        <g key={i}>
          <line x1={120 + i * 20} y1="400" x2={120 + i * 20} y2={380 - (i % 3) * 8}
            stroke="#2e7d32" strokeWidth="3" strokeLinecap="round">
            <animate attributeName="y2" values={`${380 - (i % 3) * 8};${375 - (i % 3) * 8};${380 - (i % 3) * 8}`}
              dur={`${2 + (i % 4) * 0.3}s`} repeatCount="indefinite" />
          </line>
        </g>
      ))}
      {/* Sporophytes (tall stalks with capsules) */}
      {[200, 350, 500].map((x, i) => (
        <g key={i}>
          <line x1={x} y1="390" x2={x} y2={250 - i * 20} stroke="#4caf50" strokeWidth="2" />
          {/* Capsule */}
          <ellipse cx={x} cy={245 - i * 20} rx="8" ry="14" fill="#6d4c41" stroke="#5d4037" strokeWidth="1">
            <animate attributeName="ry" values="14;15;14" dur={`${2 + i * 0.5}s`} repeatCount="indefinite" />
          </ellipse>
        </g>
      ))}
      {/* Spores releasing */}
      {Array.from({ length: 20 }, (_, i) => {
        const srcX = [200, 350, 500][i % 3];
        const srcY = [245, 225, 205][i % 3];
        return (
          <circle key={i} cx={srcX} cy={srcY} r="2" fill="#a5d6a7" opacity="0.4">
            <animate attributeName="cx" from={srcX} to={srcX + Math.sin(phase + i * 0.5) * 100 + (i % 2 === 0 ? 50 : -50)}
              dur={`${3 + i * 0.3}s`} begin={`${i * 0.3}s`} repeatCount="indefinite" />
            <animate attributeName="cy" from={srcY} to={srcY - 50 + Math.cos(phase + i) * 30}
              dur={`${3 + i * 0.3}s`} begin={`${i * 0.3}s`} repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.4;0" dur={`${3 + i * 0.3}s`} begin={`${i * 0.3}s`} repeatCount="indefinite" />
          </circle>
        );
      })}
      {/* Water droplets */}
      {[0, 1, 2].map((i) => (
        <circle key={i} cx={250 + i * 130} cy="300" r="3" fill="#42a5f5" opacity="0.3">
          <animate attributeName="cy" from="300" to="395" dur={`${1.5 + i * 0.3}s`} repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.3;0" dur={`${1.5 + i * 0.3}s`} repeatCount="indefinite" />
        </circle>
      ))}
      <text x="400" y="550" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};
